import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { Speed } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MetricCard = ({ label, value, unit, color }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    <Typography 
      variant="overline" 
      sx={{ 
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: 0.5,
        color: 'text.secondary',
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 700, 
          color: color, 
          fontSize: '1.75rem', 
          lineHeight: 1,
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Typography>
      {unit && (
        <Typography 
          component="span" 
          sx={{ 
            color: 'text.secondary', 
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {unit}
        </Typography>
      )}
    </Box>
  </Box>
);

const RiskMetricsGrid = ({ signals }) => {
  const { t } = useTranslation();
  if (!signals) return null;

  const metrics = [
    {
      label: t('risk.downRisk10d'),
      value: ((signals.down_risk_10 || 0) * 100).toFixed(1),
      unit: '%',
      color: signals.down_risk_10 > 0.7 ? '#ef4444' : signals.down_risk_10 > 0.5 ? '#f59e0b' : '#10b981',
    },
    {
      label: t('risk.downRisk20d'),
      value: ((signals.down_risk_20 || 0) * 100).toFixed(1),
      unit: '%',
      color: signals.down_risk_20 > 0.7 ? '#ef4444' : signals.down_risk_20 > 0.5 ? '#f59e0b' : '#10b981',
    },
    {
      label: t('risk.downRisk60d'),
      value: ((signals.down_risk_60 || 0) * 100).toFixed(1),
      unit: '%',
      color: signals.down_risk_60 > 0.7 ? '#ef4444' : signals.down_risk_60 > 0.5 ? '#f59e0b' : '#10b981',
    },
    {
      label: t('risk.baseExposure'),
      value: ((signals.base_exposure || 0) * 100).toFixed(1),
      unit: '%',
      color: '#3b82f6',
    },
    {
      label: t('risk.exposureCap'),
      value: ((signals.cap || 0) * 100).toFixed(1),
      unit: '%',
      color: signals.cap < 0.5 ? '#ef4444' : signals.cap < 0.7 ? '#f59e0b' : '#10b981',
    },
    {
      label: t('risk.activeExposure'),
      value: ((signals.exposure || 0) * 100).toFixed(1),
      unit: '%',
      color: signals.exposure > 0.7 ? '#10b981' : signals.exposure > 0.4 ? '#f59e0b' : '#ef4444',
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: '#ffffff',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - With Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#6366f1', 0.1),
              color: '#6366f1',
            }}
          >
            <Speed sx={{ fontSize: 16 }} />
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1,
            }}
          >
            {t('risk.riskMetrics')}
          </Typography>
        </Box>

        {/* Metrics Grid - Clean Layout */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)',
          },
          gap: 3,
          rowGap: 2.5,
        }}>
          {metrics.map((metric, idx) => (
            <MetricCard key={idx} {...metric} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RiskMetricsGrid;
